// src/pages/POSProductList.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
// import InfiniteScroll from "react-infinite-scroll-component";
import { usePOSProductsContext } from "../contexts/POSProductContext";
import ProductCard from "../../CommonComponents/ProductCard";
import { useCart } from "../contexts/cartContext";
import axiosInstance from "../../../services/axiosInstance";
import { authStorage } from "../../../utils/authStorage";
import SearchInput from "../../CommonComponents/SearchInput";
import ReactSelectField from "../../CommonComponents/ReactSelectField";
import BulkEditButton from "../../CommonComponents/BulkEditButton";
import EmptyState from "../../CommonComponents/EmptyState";
import { showErrorToast } from "../../../utils/toast";
import { playScanErrorSound, playScanSuccessSound } from "../../../utils/scanSounds";
import { Check, ChevronDown, Delete, Flame, Grid3X3, ScanLine, ShoppingCart, Star, TrendingUp, WifiOff, X } from "lucide-react";
import { PRODUCT_CATEGORY_FILTER_OPTIONS } from "../../../constants/productCategories";

// Skeleton loader
const ProductSkeletonGrid = ({ count, sidebarCollapsed = false }) => (
    <div className="relative px-4 pt-4 animate-pulse">
        <div
            className="sticky top-0 z-40 mb-4 flex w-full items-center justify-between gap-3 border-b border-gray-100 bg-white/95 px-1 py-2"
            style={{ backdropFilter: 'blur(2px)' }}
        >
            <div className="h-12 flex-1 rounded-2xl bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100" />
            <div className="h-11 w-24 shrink-0 rounded-2xl bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100" />
        </div>

        <div className="mb-5 w-full">
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-amber-50/40 shadow-sm">
                <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 px-4 py-3">
                    <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-amber-100/80" />
                        <div className="space-y-2">
                            <div className="h-3 w-28 rounded-full bg-slate-200" />
                            <div className="h-2.5 w-44 rounded-full bg-slate-100" />
                        </div>
                    </div>
                    <div className="hidden h-8 w-24 rounded-full bg-slate-100 sm:block" />
                </div>
                <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 xl:grid-cols-5">
                    {Array.from({ length: 5 }).map((_, idx) => (
                        <div key={`top-skeleton-${idx}`} className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                            <div className="relative overflow-hidden rounded-2xl bg-slate-100">
                                <div className="h-24 w-full bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100" />
                                <div className="absolute left-2 top-2 h-6 w-12 rounded-full bg-slate-800/85" />
                            </div>
                            <div className="mt-3 space-y-2">
                                <div className="h-4 w-4/5 rounded-full bg-slate-200" />
                                <div className="h-3 w-2/5 rounded-full bg-slate-100" />
                                <div className="h-3 w-1/3 rounded-full bg-slate-100" />
                            </div>
                            <div className="mt-4 h-9 rounded-xl bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100" />
                        </div>
                    ))}
                </div>
            </div>
        </div>

        <div
            className={`${sidebarCollapsed ? 'grid w-full max-w-full grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6' : 'grid grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 max-w-4xl'}`}
        >
            {Array.from({ length: count }).map((_, idx) => (
                <div
                    key={idx}
                    className="overflow-hidden rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm"
                >
                    <div className="relative overflow-hidden rounded-2xl bg-slate-100">
                        <div className="h-36 sm:h-40 md:h-44 w-full bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100" />
                        <div className="absolute left-3 top-3 h-8 w-20 rounded-full bg-slate-800/90" />
                    </div>
                    <div className="mt-4 space-y-2">
                        <div className="h-4 w-4/5 rounded-full bg-slate-200" />
                        <div className="h-3 w-1/2 rounded-full bg-slate-100" />
                    </div>
                    <div className="mt-4 space-y-2">
                        <div className="h-7 rounded-full bg-emerald-50" />
                        <div className="h-7 rounded-full bg-amber-50" />
                    </div>
                    <div className="mt-4 h-10 rounded-2xl bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100" />
                </div>
            ))}
        </div>
    </div>
);

function POSProductList({ sidebarCollapsed = false, onPaginationElementChange }) {
    // const { user } = useAuth();
    // Only use addToCart from cart context.
    const { addToCart } = useCart();
    const { products, loading, error, fetchProducts } = usePOSProductsContext();
    // Debug: log products and their stock to verify inventory source
    React.useEffect(() => {
        if (products && products.length > 0) {
            console.log("[POSProductList] Products for this user:", products.map(p => ({ id: p.id, name: p.name, stock: p.stock })));
        }
    }, [products]);
    const [searchTerm, setSearchTerm] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("All");
    const [barcodeTerm, setBarcodeTerm] = useState("");
    const [salesMode, setSalesMode] = useState("barcode");
    const itemsPerPage = 20;
    const [currentPage, setCurrentPage] = useState(1);

    const [viewMode] = useState("grid");
    const [massEditMode, setMassEditMode] = useState(false);
    const [topProducts, setTopProducts] = useState([]);
    const [topProductsCollapsed, setTopProductsCollapsed] = useState(false);
    const [topProductsRefreshKey, setTopProductsRefreshKey] = useState(0);
    const [scannedProduct, setScannedProduct] = useState(null);
    const [scanQuantity, setScanQuantity] = useState("");
    const productListTopRef = useRef(null);
    const barcodeInputRef = useRef(null);
    const barcodeBufferRef = useRef("");

    // Track quantities per product in bulk mode
    const [massEditQuantities, setMassEditQuantities] = useState({});

    // Reset to first page when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, categoryFilter]);

    // Filter products by search and sort: in-stock first
    const filteredProducts = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();

        return products
            .filter((p) => {
                const productCategory = p.category || "Others";
                const matchesSearch = !normalizedSearch
                    || p.name.toLowerCase().includes(normalizedSearch)
                    || String(p.product_code || "").toLowerCase().includes(normalizedSearch)
                    || productCategory.toLowerCase().includes(normalizedSearch);
                const matchesCategory = categoryFilter === "All" || productCategory === categoryFilter;

                return matchesSearch && matchesCategory;
            })
            .sort((a, b) => {
                const aInStock = (a.stock || 0) > 0;
                const bInStock = (b.stock || 0) > 0;
                if (aInStock === bInStock) return a.name.localeCompare(b.name);
                return aInStock ? -1 : 1;
            });
    }, [products, searchTerm, categoryFilter]);
    const productSuggestions = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        if (!normalizedSearch) return [];
        const uniqueNames = new Set();

        return products
            .map((product) => product.name)
            .filter((name) => {
                if (!name || uniqueNames.has(name)) return false;
                const matches = name.toLowerCase().includes(normalizedSearch);
                if (matches) {
                    uniqueNames.add(name);
                }
                return matches;
            })
            .slice(0, 6);
    }, [products, searchTerm]);

    useEffect(() => {
        const handleTopProductsRefresh = () => {
            setTopProductsRefreshKey((prev) => prev + 1);
        };

        window.addEventListener("pos-order-created", handleTopProductsRefresh);
        return () => window.removeEventListener("pos-order-created", handleTopProductsRefresh);
    }, []);

    useEffect(() => {
        let isMounted = true;

        const fetchTopProducts = async () => {
            try {
                const user = authStorage.getUser();
                const branchId = user?.branch_id;
                const topProductsRes = await axiosInstance.get("/admin/orders/analytics", {
                    params: branchId ? { branch_id: branchId } : undefined,
                });
                const topFive = (topProductsRes.data?.topProducts || []).slice(0, 5);
                if (!isMounted) return;
                setTopProducts(topFive);
            } catch (fetchError) {
                console.error("[POSProductList] Failed to load top products:", fetchError);
                if (isMounted) {
                    setTopProducts([]);
                }
            }
        };

        fetchTopProducts();

        return () => {
            isMounted = false;
        };
    }, [topProductsRefreshKey]);

    useEffect(() => {
        if (salesMode !== "product") return;

        const hasActiveSearch = Boolean(searchTerm.trim());
        const hasActiveCategory = categoryFilter !== "All";

        if (hasActiveSearch || hasActiveCategory) {
            setTopProductsCollapsed(true);
        } else {
            setTopProductsCollapsed(false);
        }
    }, [salesMode, searchTerm, categoryFilter]);

    useEffect(() => {
        setTopProductsCollapsed(false);
        setMassEditMode(false);
        if (salesMode === "barcode") {
            setSearchTerm("");
            window.requestAnimationFrame(() => barcodeInputRef.current?.focus());
        } else {
            setBarcodeTerm("");
        }
    }, [salesMode]);

    const handleBarcodeSubmit = useCallback((event) => {
        event?.preventDefault();
        const scannedValue = barcodeBufferRef.current.trim().toLowerCase() || barcodeTerm.trim().toLowerCase();
        if (!scannedValue) return;

        const matchedProduct = products.find((product) => {
            const values = [
                product.barcode,
                product.product_code,
                product.code,
                product.sku,
                product.id,
            ];
            return values.some((value) => String(value || "").trim().toLowerCase() === scannedValue);
        });

        if (!matchedProduct) {
            playScanErrorSound();
            showErrorToast("No product found for that barcode");
            setBarcodeTerm("");
            barcodeBufferRef.current = "";
            barcodeInputRef.current?.focus();
            return;
        }

        if ((Number(matchedProduct.stock) || 0) <= 0) {
            playScanErrorSound();
            showErrorToast(`${matchedProduct.name} is out of stock`);
            setBarcodeTerm("");
            barcodeBufferRef.current = "";
            barcodeInputRef.current?.focus();
            return;
        }

        setBarcodeTerm("");
        barcodeBufferRef.current = "";

        const wasAdded = addToCart(matchedProduct, 1);
        if (wasAdded) {
            playScanSuccessSound();
        } else {
            playScanErrorSound();
        }
        window.requestAnimationFrame(() => barcodeInputRef.current?.focus());
    }, [addToCart, barcodeTerm, products]);

    useEffect(() => {
        if (salesMode !== "barcode" || scannedProduct) return undefined;

        const handleBarcodeKeyDown = (event) => {
            if (event.ctrlKey || event.altKey || event.metaKey) return;

            const target = event.target;
            if (typeof HTMLElement !== "undefined" && target instanceof HTMLElement) {
                const isModalInteraction = target.closest("[data-pos-modal='true']");
                const isEditableTarget = target.isContentEditable
                    || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);

                if (isModalInteraction || (isEditableTarget && target !== barcodeInputRef.current)) {
                    return;
                }
            }

            if (event.key === "Enter") {
                event.preventDefault();
                handleBarcodeSubmit(event);
                return;
            }

            if (event.key === "Backspace") {
                event.preventDefault();
                barcodeBufferRef.current = barcodeBufferRef.current.slice(0, -1);
                setBarcodeTerm(barcodeBufferRef.current);
                return;
            }

            if (event.key.length === 1) {
                event.preventDefault();
                barcodeBufferRef.current += event.key;
                setBarcodeTerm(barcodeBufferRef.current);
            }
        };

        window.addEventListener("keydown", handleBarcodeKeyDown);
        return () => window.removeEventListener("keydown", handleBarcodeKeyDown);
    }, [salesMode, scannedProduct, products, handleBarcodeSubmit]);

    useEffect(() => {
        if (salesMode === "barcode" && !scannedProduct) {
            window.requestAnimationFrame(() => barcodeInputRef.current?.focus());
        }
    }, [salesMode, scannedProduct]);

    const closeScanQuantityModal = () => {
        setScannedProduct(null);
        setScanQuantity("");
        window.requestAnimationFrame(() => barcodeInputRef.current?.focus());
    };

    const appendScanQuantityDigit = (digit) => {
        setScanQuantity((prev) => {
            const next = `${prev}${digit}`.replace(/^0+(?=\d)/, "");
            return next.slice(0, 4);
        });
    };

    const handleConfirmScanQuantity = () => {
        if (!scannedProduct) return;
        const requestedQty = Number(scanQuantity);
        const availableStock = Number(scannedProduct.stock || 0);

        if (!requestedQty || requestedQty <= 0) {
            playScanErrorSound();
            showErrorToast("Enter quantity before adding to cart");
            return;
        }

        if (requestedQty > availableStock) {
            playScanErrorSound();
            showErrorToast(`Only ${availableStock} left in stock`);
            return;
        }

        const wasAdded = addToCart(scannedProduct, requestedQty);
        if (wasAdded) {
            playScanSuccessSound();
            closeScanQuantityModal();
        } else {
            playScanErrorSound();
        }
    };

    const handleAudibleAddToCart = useCallback((product, qty = 1) => {
        if (!product) {
            playScanErrorSound();
            return;
        }

        const wasAdded = addToCart(product, qty);
        if (wasAdded) {
            playScanSuccessSound();
        } else {
            playScanErrorSound();
        }
    }, [addToCart]);

    const topProductCards = useMemo(() => {
        return topProducts.map((item, index) => {
            const matchedProduct = products.find((product) => String(product.id) === String(item.product_id));
            return {
                id: item.product_id || `top-${index}`,
                name: item.product || matchedProduct?.name || `Product ${index + 1}`,
                sold: Number(item.totalQty || 0),
                image: matchedProduct?.image || null,
                price: matchedProduct?.price ?? null,
                stock: matchedProduct?.stock ?? 0,
                product: matchedProduct || null,
            };
        });
    }, [products, topProducts]);

    const selectedCategoryOption = PRODUCT_CATEGORY_FILTER_OPTIONS.find((option) => option.value === categoryFilter)
        || PRODUCT_CATEGORY_FILTER_OPTIONS[0];

    // Pagination logic
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
    const paginatedProducts = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredProducts.slice(start, start + itemsPerPage);
    }, [filteredProducts, currentPage]);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages || page === currentPage) return;
        setCurrentPage(page);
        window.requestAnimationFrame(() => {
            productListTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    };


    // Toggle mass edit mode
    const handleToggleMassEdit = () => {
        setMassEditMode((prev) => !prev);

        if (!massEditMode) {
            // Initialize quantities for bulk edit, but exclude products with stock <= 0
            const initialQuantities = {};
            products
                .filter((p) => p.stock > 0) // only include products with stock
                .forEach((p) => (initialQuantities[p.id] = 0));
            setMassEditQuantities(initialQuantities);
        }
    };

    // Handle quantity change for mass edit mode
    const handleQuantityChange = (productId, value) => {
        setMassEditQuantities(prev => ({ ...prev, [productId]: value }));
    };

    // ...existing code...

    if (loading) return <ProductSkeletonGrid count={itemsPerPage} sidebarCollapsed={sidebarCollapsed} />;

    if (error)
        return (
            <EmptyState
                icon={WifiOff}
                title="Failed to Load Products"
                message={error.message || "Check your internet connection and try again."}
                onRetry={fetchProducts}
                retryLabel="Retry"
            />
        );

    return (
        <div ref={productListTopRef} className="relative px-4 pt-4">
            {/* Fixed search and bulk edit at top of grid container */}
            <div
                className="sticky top-0 z-40 mb-4 grid w-full min-w-0 gap-3 border-b border-gray-100 bg-white/95 px-1 py-2 xl:grid-cols-[auto,minmax(0,1fr)] xl:items-center"
                style={{ backdropFilter: 'blur(2px)' }}
            >
                <div className="inline-flex w-full min-w-0 rounded-2xl border border-slate-200 bg-slate-50 p-1 shadow-sm sm:w-fit sm:shrink-0">
                    <button
                        type="button"
                        onClick={() => setSalesMode("barcode")}
                        className={`inline-flex min-w-0 flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition sm:flex-none ${salesMode === "barcode" ? "!text-white shadow-sm [&_*]:!text-white" : "text-slate-600 hover:bg-white"}`}
                        style={salesMode === "barcode" ? { background: "var(--color-primary)" } : undefined}
                    >
                        <ScanLine className="h-4 w-4" />
                        <span className="truncate">Barcode Mode</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setSalesMode("product")}
                        className={`inline-flex min-w-0 flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition sm:flex-none ${salesMode === "product" ? "!text-white shadow-sm [&_*]:!text-white" : "text-slate-600 hover:bg-white"}`}
                        style={salesMode === "product" ? { background: "var(--color-primary)" } : undefined}
                    >
                        <Grid3X3 className="h-4 w-4" />
                        <span className="truncate">Product Mode</span>
                    </button>
                </div>
                <div className="min-w-0">
                    {salesMode === "product" ? (
                        <div className="grid min-w-0 gap-3 xl:grid-cols-[minmax(180px,240px),minmax(220px,1fr),44px] xl:items-center">
                            <div className="min-w-0">
                                <ReactSelectField
                                    options={PRODUCT_CATEGORY_FILTER_OPTIONS}
                                    value={selectedCategoryOption}
                                    onChange={(option) => setCategoryFilter(option?.value || "All")}
                                    placeholder="All Categories"
                                    isSearchable
                                    ariaLabel="Filter POS products by category"
                                />
                            </div>
                            <div className="grid min-w-0 grid-cols-[minmax(0,1fr),44px] items-center gap-2 xl:contents">
                                <div className="min-w-0">
                                    <SearchInput
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        onSuggestionSelect={(productName) => setSearchTerm(productName)}
                                        suggestions={productSuggestions}
                                        placeholder="Search products..."
                                        className="group relative w-full"
                                    />
                                </div>
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center justify-self-end">
                                    <BulkEditButton
                                        onClick={handleToggleMassEdit}
                                        title={massEditMode ? "Done" : "Bulk Edit"}
                                        isActive={massEditMode}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleBarcodeSubmit} className="grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr),auto] sm:items-center">
                            <div className="relative min-w-0">
                                <ScanLine className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                                <input
                                    ref={barcodeInputRef}
                                    type="text"
                                    inputMode="none"
                                    autoComplete="off"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    readOnly
                                    value={barcodeTerm}
                                    onFocus={(event) => {
                                        event.target.style.borderColor = "var(--color-primary)";
                                        event.target.style.boxShadow = "0 0 0 4px color-mix(in srgb, var(--color-primary) 14%, white), 0 12px 28px rgba(15,23,42,0.08)";
                                    }}
                                    placeholder="Ready for barcode scanner..."
                                    className="w-full rounded-2xl border border-slate-200 bg-white/90 py-3 pl-12 pr-4 text-sm text-slate-900 shadow-[0_10px_25px_rgba(15,23,42,0.05)] transition-all duration-200 placeholder:text-slate-400 focus:outline-none"
                                    onClick={() => barcodeInputRef.current?.focus()}
                                    onBlur={(event) => {
                                        event.target.style.borderColor = "#e2e8f0";
                                        event.target.style.boxShadow = "0 10px 25px rgba(15,23,42,0.05)";
                                        if (!scannedProduct) {
                                            window.requestAnimationFrame(() => {
                                                const activeElement = document.activeElement;
                                                const hasModalOpen = document.querySelector("[data-pos-modal='true']");
                                                const activeElementIsEditable = activeElement instanceof HTMLElement
                                                    && activeElement !== barcodeInputRef.current
                                                    && (
                                                        activeElement.isContentEditable
                                                        || ["INPUT", "TEXTAREA", "SELECT", "BUTTON"].includes(activeElement.tagName)
                                                    );

                                                if (!hasModalOpen && !activeElementIsEditable) {
                                                    barcodeInputRef.current?.focus();
                                                }
                                            });
                                        }
                                    }}
                                />
                            </div>
                            <button
                                type="submit"
                                className="shrink-0 rounded-xl px-4 py-3 text-xs font-semibold text-white shadow-sm transition hover:opacity-90"
                                style={{ background: "var(--color-primary)" }}
                            >
                                Scan
                            </button>
                        </form>
                    )}
                </div>
            </div>

            {topProductCards.length > 0 && (
                <section className="mb-5 w-full">
                    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-amber-50/50 shadow-sm">
                        <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 px-4 py-3">
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                                        <Flame className="h-4 w-4" />
                                    </span>
                                    <h2 className="text-sm font-semibold text-slate-900">Top 5 Products</h2>
                                </div>
                                <p className="mt-1 text-xs text-slate-500">
                                    Best-selling items based on recorded sales.
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                                    <TrendingUp className="h-3.5 w-3.5" />
                                    Popular now
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setTopProductsCollapsed((prev) => !prev)}
                                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
                                >
                                    {topProductsCollapsed ? "Show" : "Hide"}
                                    <ChevronDown className={`h-3.5 w-3.5 transition ${topProductsCollapsed ? "-rotate-90" : "rotate-0"}`} />
                                </button>
                            </div>
                        </div>

                        {!topProductsCollapsed && (
                            <div
                                className={`grid gap-3 p-4 ${
                                    sidebarCollapsed
                                        ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
                                        : "grid-cols-1 sm:grid-cols-2 xl:grid-cols-5"
                                }`}
                            >
                                {topProductCards.map((product, index) => (
                                    <div
                                        key={product.id}
                                        className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                                    >
                                        <div className="relative overflow-hidden rounded-2xl bg-slate-100">
                                            {product.image ? (
                                                <img
                                                    src={product.image}
                                                    alt={product.name}
                                                    className="h-24 w-full object-cover"
                                                />
                                            ) : (
                                                <div className="flex h-24 items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 text-slate-400">
                                                    <ShoppingCart className="h-7 w-7" />
                                                </div>
                                            )}

                                            <div className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-slate-900/90 px-2 py-1 text-[11px] font-semibold text-white">
                                                <Star className="h-3 w-3 text-amber-300" />
                                                #{index + 1}
                                            </div>
                                        </div>

                                        <div className="mt-3 min-h-[88px] space-y-1">
                                            <p className="line-clamp-2 text-sm font-semibold text-slate-900">
                                                {product.name}
                                            </p>
                                            <p className="text-xs text-slate-500">
                                                {product.sold} unit{product.sold === 1 ? "" : "s"} sold
                                            </p>
                                            {product.price !== null && (
                                                <p className="text-xs font-medium text-slate-700">
                                                    ₱{Number(product.price).toLocaleString(undefined, {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </p>
                                            )}
                                            {product.stock > 0 && (
                                                <p className="text-xs font-medium text-emerald-600">
                                                    {product.stock} left in stock
                                                </p>
                                            )}
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => product.product && product.stock > 0 && handleAudibleAddToCart(product.product)}
                                            disabled={!product.product || product.stock <= 0}
                                            className={`mt-auto flex w-full items-center justify-center rounded-xl px-3 py-2 text-xs font-semibold transition ${product.product && product.stock > 0
                                                    ? 'text-white shadow-sm hover:opacity-90'
                                                    : 'cursor-not-allowed bg-slate-100 text-slate-400'
                                                }`}
                                            style={product.product && product.stock > 0 ? { background: 'var(--color-primary)' } : {}}
                                        >
                                            {product.product && product.stock > 0 ? 'Add to Cart' : 'Out of Stock'}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            )}

            {salesMode === "barcode" && topProductCards.length === 0 && (
                <EmptyState icon={ShoppingCart} message="No top products to show yet." />
            )}

            {/* Pagination Controls - Fixed at bottom INSIDE product grid container */}
            {salesMode === "product" && (
            <div className="relative">
                <div
                    className={`${viewMode === "grid" ? `${sidebarCollapsed ? 'grid w-full max-w-full grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6' : 'grid w-full max-w-full grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5'}` : "flex flex-col gap-2"}`}
                >
                    {paginatedProducts.length > 0 ? (
                        paginatedProducts.map((product, idx) => (
                            <ProductCard
                                key={`${product.id}-${idx}`}
                                product={product}
                                massEditMode={massEditMode}
                                viewMode={viewMode}
                                quantity={massEditQuantities[product.id] || 0}
                                onQuantityChange={(id, val) => handleQuantityChange(id, val)}
                                showAddToCart={true}
                                onAddToCart={handleAudibleAddToCart}
                            />
                        ))
                    ) : (
                        <EmptyState icon={ShoppingCart} message="No products found" />
                    )}
                </div>

                {/* Keep navigation compact regardless of the number of pages. */}
                {totalPages > 1 && (
                    <nav ref={onPaginationElementChange} aria-label="Product pagination" className="sticky bottom-0 z-20 mt-6 flex w-full min-w-0 flex-col items-center justify-center gap-2 border-t border-gray-200 bg-white px-2 py-2 text-center shadow-[0_-4px_12px_rgba(15,23,42,0.06)] md:flex-row md:flex-wrap md:justify-between md:gap-3 md:py-3 md:text-left">
                        <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
                            <button
                                type="button"
                                className="rounded-lg px-3 py-2 text-xs font-semibold transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                                style={{ background: 'var(--color-primary)', color: 'var(--color-on-primary, #fff)' }}
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                Prev
                            </button>
                            <span className="text-xs text-slate-600" aria-live="polite">Page {currentPage} of {totalPages}</span>
                            <button
                                type="button"
                                className="rounded-lg px-3 py-2 text-xs font-semibold transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                                style={{ background: 'var(--color-primary)', color: 'var(--color-on-primary, #fff)' }}
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </div>
                        <p className="text-xs text-slate-500 md:order-first">
                            Showing {filteredProducts.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}
                            –{Math.min(currentPage * itemsPerPage, filteredProducts.length)} of {filteredProducts.length}
                        </p>
                    </nav>
                )}
            </div>
            )}

            {scannedProduct && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 px-4 py-6 backdrop-blur-sm">
                    <div className="w-full max-w-md overflow-hidden rounded-[28px] bg-white shadow-[0_28px_90px_rgba(15,23,42,0.32)]">
                        <div className="border-b border-slate-200 px-5 py-4">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3">
                                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: "var(--color-primary)" }}>
                                        <ScanLine className="h-6 w-6" />
                                    </div>
                                    <div className="min-w-0">
                                        <h2 className="text-xl font-semibold text-slate-950">Enter Quantity</h2>
                                        <p className="mt-1 truncate text-sm font-medium text-slate-700">{scannedProduct.name}</p>
                                        <p className="text-xs text-slate-500">{Number(scannedProduct.stock || 0)} left in stock</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={closeScanQuantityModal}
                                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
                                    aria-label="Cancel scanned quantity"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        <div className="space-y-4 p-5">
                            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                                <div className="mb-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-right text-4xl font-semibold tracking-tight text-slate-950 shadow-inner">
                                    {scanQuantity || "0"}
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                                        <button
                                            key={digit}
                                            type="button"
                                            onClick={() => appendScanQuantityDigit(String(digit))}
                                            className="h-14 rounded-2xl border border-slate-200 bg-white text-xl font-semibold text-slate-900 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:bg-slate-50"
                                        >
                                            {digit}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => setScanQuantity("")}
                                        className="h-14 rounded-2xl border border-slate-200 bg-white text-sm font-semibold text-slate-500 shadow-sm transition hover:bg-slate-50"
                                    >
                                        Clear
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => appendScanQuantityDigit("0")}
                                        className="h-14 rounded-2xl border border-slate-200 bg-white text-xl font-semibold text-slate-900 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:bg-slate-50"
                                    >
                                        0
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setScanQuantity((prev) => prev.slice(0, -1))}
                                        className="flex h-14 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50"
                                        aria-label="Delete last digit"
                                    >
                                        <Delete className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={closeScanQuantityModal}
                                    className="h-12 rounded-2xl border border-slate-200 bg-white text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirmScanQuantity}
                                    disabled={!Number(scanQuantity)}
                                    className="flex h-12 items-center justify-center gap-2 rounded-2xl text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
                                    style={Number(scanQuantity) ? { background: "var(--color-primary)" } : undefined}
                                    onMouseOver={Number(scanQuantity) ? (event) => {
                                        event.currentTarget.style.background = "color-mix(in srgb, var(--color-primary) 82%, black)";
                                    } : undefined}
                                    onMouseOut={Number(scanQuantity) ? (event) => {
                                        event.currentTarget.style.background = "var(--color-primary)";
                                    } : undefined}
                                >
                                    <Check className="h-4 w-4" />
                                    Add to Cart
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ...product grid and fixed pagination now handled above... */}
        </div>
    );
}
export default POSProductList;
